import { useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { Heading, Text, Button } from '@shinkansen/sakura'
import { BrandLockup } from '~/components/BrandLockup'
import { requestPasswordReset, requestPhoneOtp, verifyPhoneOtp } from '~/auth/auth-client'
import styles from './auth.module.css'

type RecoveryMode = 'phone' | 'email'
type RecoveryStep = 'identifier' | 'phoneCode' | 'emailSent'

const NEUTRAL_MESSAGE = 'If an account exists, reset instructions have been sent.'

export function ForgotPasswordPage() {
  const [mode, setMode] = useState<RecoveryMode>('phone')
  const [step, setStep] = useState<RecoveryStep>('identifier')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    setError('')
    setMessage('')

    if (mode === 'phone') {
      if (phone.replace(/\D/g, '').length < 8) {
        setError('Enter a valid phone number')
        return
      }
      setIsSubmitting(true)
      try {
        const result = await requestPhoneOtp(phone)
        if (!result.ok) {
          setError(result.error === 'otp_unavailable'
            ? 'Phone verification is temporarily unavailable'
            : 'Unable to send verification code')
          return
        }
        setMessage(`Code sent to ${result.to ?? 'your phone'}.`)
        setStep('phoneCode')
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    setIsSubmitting(true)
    try {
      await requestPasswordReset(email)
      setMessage(NEUTRAL_MESSAGE)
      setStep('emailSent')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    setError('')
    if (otpCode.replace(/\D/g, '').length !== 6) {
      setError('Wrong code - enter the 6-digit reset code')
      return
    }

    setIsSubmitting(true)
    try {
      const verification = await verifyPhoneOtp(phone, otpCode)
      if (!verification.ok) {
        setError('Wrong code - enter the 6-digit reset code')
        return
      }
      setMessage('Phone verified. WorkOS password reset still requires the account email.')
      setMode('email')
      setStep('identifier')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <span className={styles.bgNumber}>03</span>

      <div className={styles.container}>
        <header className={styles.header}>
          <BrandLockup variant="auth" meta="ACCOUNT RECOVERY" />
        </header>

        <main className={styles.main}>
          <Heading level="h2" size="xl" className={styles.formTitle}>
            RESET PASSWORD
          </Heading>

          {error && <div className={styles.error}>{error}</div>}
          {message && <div className={styles.success}>{message}</div>}

          {step === 'identifier' && (
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.modeSwitch} aria-label="Choose reset method">
                <button
                  className={`${styles.modeButton} ${mode === 'phone' ? styles.modeButtonActive : ''}`}
                  type="button"
                  onClick={() => setMode('phone')}
                >
                  PHONE
                </button>
                <button
                  className={`${styles.modeButton} ${mode === 'email' ? styles.modeButtonActive : ''}`}
                  type="button"
                  onClick={() => setMode('email')}
                >
                  EMAIL
                </button>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="recoveryIdentifier">
                  {mode === 'phone' ? 'PHONE NUMBER' : 'EMAIL'}
                </label>
                {mode === 'phone' ? (
                  <>
                    <div className={styles.identifierRow}>
                      <span className={styles.countryPrefix}>ID +62</span>
                      <input
                        className={`${styles.input} ${styles.inlineInput}`}
                        id="recoveryIdentifier"
                        name="recoveryIdentifier"
                        type="tel"
                        inputMode="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        autoComplete="tel"
                        autoFocus
                        required
                      />
                    </div>
                    <span className={styles.helperText}>
                      Twilio verifies this phone. WorkOS password reset still uses email.
                    </span>
                  </>
                ) : (
                  <input
                    className={styles.input}
                    id="recoveryIdentifier"
                    name="recoveryIdentifier"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                )}
              </div>

              <Button type="submit" variant="accent" size="lg" width="full" disabled={isSubmitting}>
                {isSubmitting ? 'SENDING...' : 'SEND CODE / LINK'}
              </Button>
            </form>
          )}

          {step === 'phoneCode' && (
            <form className={styles.form} onSubmit={handleCodeSubmit}>
              <div className={styles.noticeCard}>
                <span className={styles.noticeTitle}>ENTER RESET CODE</span>
                <Text variant="muted" size="sm">
                  Enter the 6-digit Twilio code sent to ID +62 {phone}.
                </Text>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="resetCode">
                  VERIFICATION CODE
                </label>
                <input
                  className={styles.otpInput}
                  id="resetCode"
                  name="resetCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
                <span className={styles.helperText}>
                  After phone verification, use your WorkOS email for the reset link.
                </span>
              </div>

              <button
                className={styles.inlineAction}
                type="button"
                onClick={() => {
                  setMode('email')
                  setStep('identifier')
                }}
              >
                USE EMAIL LINK INSTEAD
              </button>

              <Button type="submit" variant="accent" size="lg" width="full" disabled={isSubmitting}>
                {isSubmitting ? 'VERIFYING...' : 'VERIFY CODE'}
              </Button>
            </form>
          )}

          {step === 'emailSent' && (
            <div className={styles.noticeCard}>
              <span className={styles.noticeTitle}>CHECK YOUR EMAIL</span>
              <Text variant="muted" size="sm">
                We sent reset instructions to {email}. The link expires in 15 minutes.
              </Text>
              <button className={styles.secondaryButton} type="button" onClick={() => setStep('identifier')}>
                RESEND EMAIL
              </button>
            </div>
          )}

          <div className={styles.backToLogin}>
            <Link className={styles.backLink} to="/login" search={{ reset: undefined }}>
              BACK TO LOGIN
            </Link>
          </div>
        </main>
      </div>
    </div>
  )
}
